import type { CSSProperties, ReactNode } from 'react';

type Props = {
  id?: string;
  label?: string;
  title?: ReactNode;
  kicker?: ReactNode;
  children: ReactNode;
  background?: 'bg' | 'paper';
  noTopBorder?: boolean;
  style?: CSSProperties;
};

// Reusable wrapper that gives every landing-page section the same chrome:
// thin black top border, eyebrow label, oversized display title, kicker copy,
// max-width container.
export function Section({ id, label, title, kicker, children, background = 'bg', noTopBorder, style }: Props): JSX.Element {
  const bgClass = background === 'paper' ? 'bg-paper' : 'bg-bg';
  return (
    <section
      id={id}
      className={`${bgClass} px-8 py-[72px] ${noTopBorder ? '' : 'border-t-2 border-ink'}`}
      style={style}
    >
      <div className="mx-auto max-w-[1280px]">
        {label && (
          <div className="mb-4 flex items-center gap-2.5">
            <span className="h-2 w-2 rounded-full bg-beak" />
            <span className="font-mono text-xs uppercase tracking-[0.16em] text-ink-soft">{label}</span>
          </div>
        )}
        {title && (
          <h2
            className="m-0 mb-3 font-display font-extrabold tracking-[-0.03em] text-ink"
            style={{ fontSize: 'clamp(36px, 5vw, 64px)', lineHeight: 1.02, maxWidth: 900, textWrap: 'balance' as CSSProperties['textWrap'] }}
          >
            {title}
          </h2>
        )}
        {kicker && (
          <p className="mt-0 mb-10 max-w-[720px] text-lg leading-snug text-ink-soft">{kicker}</p>
        )}
        {children}
      </div>
    </section>
  );
}
